import { makeAutoObservable, runInAction } from 'mobx';
import { applicationApi } from '@/services/applicationApi';
import type {
  AdminAppCatalogApp,
  AdminAppCatalogVersion,
  AdminVersionActionRequest,
  AppCatalogAppItem,
  AppCatalogStatus,
  AppCatalogVersionItem,
  CreateAdminAppCatalogAppRequest,
  CreateAdminAppVersionRequest,
  UpdateAdminAppCatalogAppRequest,
} from '@/types';

const APP_STATUSES: AppCatalogStatus[] = [
  'draft',
  'published',
  'hidden',
  'disabled',
  'archived',
];

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {};
}

function stringField(record: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
  }
  return '';
}

function numberField(record: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
}

function timestampField(record: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    if (record[key] !== undefined) {
      return record[key];
    }
  }
  return undefined;
}

function stringArrayField(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return Array.isArray(value)
    ? value
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
    : [];
}

function normalizeStatus(value: string): AppCatalogStatus {
  return APP_STATUSES.includes(value as AppCatalogStatus)
    ? (value as AppCatalogStatus)
    : 'draft';
}

function transformApp(app: AdminAppCatalogApp): AppCatalogAppItem {
  const record = asRecord(app);
  const appId = stringField(record, 'appId', 'app_id');
  const displayNameKey = stringField(
    record,
    'displayNameKey',
    'display_name_key',
  );
  const fallbackDisplayName = stringField(
    record,
    'fallbackDisplayName',
    'fallback_display_name',
    'displayName',
  );
  const displayName =
    fallbackDisplayName.trim() ||
    displayNameKey.trim() ||
    appId ||
    '未命名应用';
  const descriptionKey = stringField(
    record,
    'descriptionKey',
    'description_key',
  );
  const fallbackDescription = stringField(
    record,
    'fallbackDescription',
    'fallback_description',
    'description',
  );

  return {
    ...app,
    id: appId,
    appId,
    displayNameKey,
    descriptionKey,
    fallbackDisplayName,
    fallbackDescription,
    displayName,
    description: fallbackDescription,
    categories: stringArrayField(record, 'categories'),
    iconUrl: stringField(record, 'iconUrl', 'icon_url'),
    latestVersionId: stringField(
      record,
      'latestVersionId',
      'latest_version_id',
    ),
    status: normalizeStatus(stringField(record, 'status')),
    createdAt: timestampField(record, 'createdAt', 'created_at'),
    updatedAt: timestampField(record, 'updatedAt', 'updated_at'),
  };
}

function transformVersion(
  version: AdminAppCatalogVersion,
): AppCatalogVersionItem {
  const record = asRecord(version);
  const appId = stringField(record, 'appId', 'app_id');
  const versionId = stringField(record, 'versionId', 'version_id');

  return {
    ...version,
    id: `${appId}:${versionId}`,
    appId,
    versionId,
    artifactKind: stringField(record, 'artifactKind', 'artifact_kind') || 'zip',
    artifactUrl: stringField(record, 'artifactUrl', 'artifact_url'),
    artifactSha256: stringField(record, 'artifactSha256', 'artifact_sha256'),
    artifactSizeBytes: numberField(
      record,
      'artifactSizeBytes',
      'artifact_size_bytes',
    ),
    artifactSignatureRef: stringField(
      record,
      'artifactSignatureRef',
      'artifact_signature_ref',
    ),
    gitSha: stringField(record, 'gitSha', 'git_sha'),
    manifestDigest: stringField(record, 'manifestDigest', 'manifest_digest'),
    releaseNotes: stringField(record, 'releaseNotes', 'release_notes'),
    runtimeBootstrap:
      stringField(record, 'runtimeBootstrap', 'runtime_bootstrap') ||
      'bootstrap.sh',
    runtimeHealthcheckPath:
      stringField(
        record,
        'runtimeHealthcheckPath',
        'runtime_healthcheck_path',
      ) || '/',
    status: normalizeStatus(stringField(record, 'status')),
    createdAt: timestampField(record, 'createdAt', 'created_at'),
    updatedAt: timestampField(record, 'updatedAt', 'updated_at'),
    publishedAt: timestampField(record, 'publishedAt', 'published_at'),
    archivedAt: timestampField(record, 'archivedAt', 'archived_at'),
  };
}

class ApplicationStore {
  apps: AppCatalogAppItem[] = [];
  appsLoading = false;
  appsKeyword = '';

  activeApp: AppCatalogAppItem | null = null;
  versions: AppCatalogVersionItem[] = [];
  versionsLoading = false;

  savingApp = false;
  savingVersion = false;
  actionLoadingKey = '';

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get filteredApps() {
    const keyword = this.appsKeyword.trim().toLowerCase();
    if (!keyword) {
      return this.apps;
    }

    return this.apps.filter((app) => {
      const haystack = [
        app.appId,
        app.displayName,
        app.description,
        app.displayNameKey,
        app.descriptionKey,
        app.fallbackDisplayName,
        app.fallbackDescription,
        app.status,
        app.latestVersionId,
        ...(app.categories ?? []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }

  get appCounts() {
    return this.apps.reduce(
      (counts, app) => {
        counts.total += 1;
        counts[app.status] += 1;
        return counts;
      },
      {
        total: 0,
        draft: 0,
        published: 0,
        hidden: 0,
        disabled: 0,
        archived: 0,
      },
    );
  }

  setAppsKeyword(keyword: string) {
    this.appsKeyword = keyword;
  }

  async fetchApps() {
    this.appsLoading = true;
    try {
      const response = await applicationApi.listApps();
      runInAction(() => {
        this.apps = (response.apps ?? []).map(transformApp);
        this.appsLoading = false;
        if (this.activeApp) {
          this.activeApp =
            this.apps.find((app) => app.appId === this.activeApp?.appId) ??
            this.activeApp;
        }
      });
    } catch (error) {
      runInAction(() => {
        this.appsLoading = false;
      });
      throw error;
    }
  }

  async createApp(data: CreateAdminAppCatalogAppRequest) {
    this.savingApp = true;
    try {
      const response = await applicationApi.createApp(data);
      const created = transformApp(response.app);
      runInAction(() => {
        this.apps = [
          created,
          ...this.apps.filter((app) => app.id !== created.id),
        ];
        this.savingApp = false;
      });
      return created;
    } catch (error) {
      runInAction(() => {
        this.savingApp = false;
      });
      throw error;
    }
  }

  async updateApp(data: UpdateAdminAppCatalogAppRequest) {
    this.savingApp = true;
    try {
      const response = await applicationApi.updateApp(data);
      const updated = transformApp(response.app);
      runInAction(() => {
        this.upsertApp(updated);
        this.savingApp = false;
      });
      return updated;
    } catch (error) {
      runInAction(() => {
        this.savingApp = false;
      });
      throw error;
    }
  }

  openVersions(app: AppCatalogAppItem) {
    this.activeApp = app;
    this.versions = [];
    void this.fetchVersions(app.appId);
  }

  closeVersions() {
    this.activeApp = null;
    this.versions = [];
    this.versionsLoading = false;
  }

  async fetchVersions(appId = this.activeApp?.appId) {
    if (!appId) {
      return;
    }
    this.versionsLoading = true;
    try {
      const response = await applicationApi.listVersions(appId);
      runInAction(() => {
        this.versions = (response.versions ?? []).map(transformVersion);
        this.versionsLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.versionsLoading = false;
      });
      throw error;
    }
  }

  async createVersion(data: CreateAdminAppVersionRequest) {
    this.savingVersion = true;
    try {
      const response = await applicationApi.createVersion(data);
      const created = transformVersion(response.version);
      runInAction(() => {
        this.upsertVersion(created);
        this.savingVersion = false;
      });
      return created;
    } catch (error) {
      runInAction(() => {
        this.savingVersion = false;
      });
      throw error;
    }
  }

  async publishVersion(data: AdminVersionActionRequest) {
    return this.runVersionAction(
      `publish:${data.appId}:${data.versionId}`,
      async () => {
        const response = await applicationApi.publishVersion(data);
        return {
          app: response.app,
          version: response.version,
        };
      },
    );
  }

  async setLatestVersion(data: AdminVersionActionRequest) {
    return this.runVersionAction(
      `latest:${data.appId}:${data.versionId}`,
      async () => {
        const response = await applicationApi.setLatestVersion(data);
        return {
          app: response.app,
        };
      },
    );
  }

  async archiveVersion(data: AdminVersionActionRequest) {
    return this.runVersionAction(
      `archive:${data.appId}:${data.versionId}`,
      async () => {
        const response = await applicationApi.archiveVersion(data);
        return {
          version: response.version,
        };
      },
    );
  }

  private async runVersionAction(
    key: string,
    action: () => Promise<{
      app?: AdminAppCatalogApp;
      version?: AdminAppCatalogVersion;
    }>,
  ) {
    this.actionLoadingKey = key;
    try {
      const response = await action();
      runInAction(() => {
        if (response.app) {
          this.upsertApp(transformApp(response.app));
        }
        if (response.version) {
          this.upsertVersion(transformVersion(response.version));
        }
        this.actionLoadingKey = '';
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.actionLoadingKey = '';
      });
      throw error;
    }
  }

  private upsertApp(app: AppCatalogAppItem) {
    const exists = this.apps.some((item) => item.id === app.id);
    this.apps = exists
      ? this.apps.map((item) => (item.id === app.id ? app : item))
      : [app, ...this.apps];
    if (this.activeApp?.appId === app.appId) {
      this.activeApp = app;
    }
  }

  private upsertVersion(version: AppCatalogVersionItem) {
    const exists = this.versions.some((item) => item.id === version.id);
    this.versions = exists
      ? this.versions.map((item) => (item.id === version.id ? version : item))
      : [version, ...this.versions];
  }
}

export const applicationStore = new ApplicationStore();
